import {
  QueryDocumentSnapshot,
  Timestamp,
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from 'firebase/firestore';
import { db, functions } from '../config/firebaseApp';
import { toast } from 'react-toastify';
import { shouldShowButton } from '../pages/admin/dashboard/dashboardAccessControl';
import { httpsCallable } from 'firebase/functions';
import { ICurriculumMetaInfo, ICurriculumMilestoneModel } from '../store/curriculum/curriculumModel';
import { ISortModel } from '../utils/common';

export interface ICurriculumData {
  id?: string;
  careerPathId: string;
  trainerId: string;
  createdBy: string;
  description: string;
  name: string;
  createdAt?: Timestamp;
  milestones: ICurriculumMilestoneModel[];
}

export const addNewCurriculum = async (curriculumData: ICurriculumData, userRole?: string) => {
  try {
    if (!curriculumData) {
      throw new Error();
    }
    const hasPermission = shouldShowButton('createNewCurriculum', userRole);
    if (!hasPermission) {
      throw new Error('You do not have permission to create a Curriculum');
    }

    const response: any = await httpsCallable(functions, 'createNewCurriculumV2')(curriculumData);
    if (response.data.success) {
      toast.success('New Curriculum Created Successfully!');
    }
  } catch (err: any) {
    toast.error('Error Creating Curriculum!');
  }
};

export const pseudoAddCurriculum = async () => {
  try {
    const curriculumData = null;
    await httpsCallable(functions, 'createNewCurriculumV2')(curriculumData);
  } catch (err: any) {
    // err
  }
};

export const getCurriculumsByCareerPathId = async (careerPathId: string) => {
  try {
    if (!careerPathId) {
      throw new Error();
    }

    const collectionRef = collection(db, 'curriculum');
    const q = query(collectionRef, where('careerPathId', '==', careerPathId));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const list: any = [];
      querySnapshot.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() });
      });
      return list;
    } else {
      return [];
    }
  } catch (err: any) {
    toast.error('Error Retrieving Curriculums!');
  }
};

export const getCurriculumInfoByCurriculumId = async (curriculumId: string) => {
  try {
    if (!curriculumId) {
      throw new Error();
    }

    const curriculumDocRef = doc(db, 'curriculum', curriculumId);
    const curriculumDoc = await getDoc(curriculumDocRef);

    if (curriculumDoc.exists()) {
      return { id: curriculumDoc.id, ...curriculumDoc.data() };
    } else {
      return {};
    }
  } catch (error: any) {
    toast.error('Error Retrieving Curriculum Data!');
  }
};

const paginationCursors: QueryDocumentSnapshot<any>[] = [];

export const getCurriculumList = async (page: number, sortModel: ISortModel[]) => {
  const pageSize = 10;
  try {
    const curriculumRef = collection(db, 'curriculum');
    const constraints: any[] = [];

    let orderField = 'createdAt';
    let orderDirection: 'asc' | 'desc' = 'desc';

    if (sortModel.length > 0) {
      orderField = sortModel[0].field;
      orderDirection = sortModel[0].sort;
    }

    constraints.push(orderBy(orderField, orderDirection));

    if (page > 1 && paginationCursors[page - 2]) {
      constraints.push(startAfter(paginationCursors[page - 2]));
    }

    constraints.push(limit(pageSize));

    const paginatedQuery = query(curriculumRef, ...constraints);

    const countSnapshot = await getCountFromServer(query(curriculumRef));
    const totalRecords = countSnapshot.data().count;
    const totalPages = Math.ceil(totalRecords / pageSize);

    const snapshot = await getDocs(paginatedQuery);

    if (snapshot.empty) {
      return {
        totalRecords: 0,
        currentPage: page,
        curriculums: [],
        totalPages: 0,
      };
    }

    if (page > paginationCursors.length) {
      paginationCursors.push(snapshot.docs[snapshot.docs.length - 1]);
    }

    const curriculums = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    const result: ICurriculumMetaInfo = {
      totalRecords,
      currentPage: page,
      curriculums,
      totalPages,
    };

    return result;
  } catch (err: any) {
    toast.error('Error Retrieving Curriculum Data!');
    return {
      totalRecords: 0,
      currentPage: page,
      curriculums: [],
      totalPages: 0,
    };
  }
};

export const deleteCurriculum = async (curriculumId: string) => {
  try {
    const response: any = await httpsCallable(functions, 'deleteCurriculumV2')({ id: curriculumId });
    if (response.data.success) {
      toast.success('Curriculum Deleted Successfully!');
    }
  } catch (error: any) {
    toast.error('Error Deleting Curriculum!');
  }
};

export const editCurriculumById = async (curriculumId: string, payload: any) => {
  try {
    if (!curriculumId) {
      throw new Error();
    }

    const curriculumDocRef = doc(db, 'curriculum', curriculumId);
    const curriculumDoc = await getDoc(curriculumDocRef);

    if (curriculumDoc.exists()) {
      const data = { ...payload, id: curriculumId };

      const response: any = await httpsCallable(functions, 'editCurriculumV2')(data);

      if (response.data.success) {
        toast.success('Curriculum Updated Successfully!');
      } else {
        toast.error('Error Updating Curriculum!');
      }
    }
  } catch (error: any) {
    toast.error('Error Updating Curriculum!');
  }
};

export const isTrainerAssignedToCurriculum = async (trainerId: string) => {
  try {
    if (!trainerId) {
      throw new Error();
    }

    const collectionRef = collection(db, 'curriculum');
    const q = query(collectionRef, where('trainerId', '==', trainerId));
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty ? true : false;
  } catch (err: any) {
    toast.error('Error Retrieving Data!');
  }
};

export const getMultipleCurriculumByIds = async (curriculumIds: string[]) => {
  try {
    if (!curriculumIds.length) {
      throw new Error();
    }

    const curriculumRef = collection(db, 'curriculum');
    const querySnapshot = await getDocs(query(curriculumRef));
    const documents = querySnapshot.docs.filter((doc) => curriculumIds.includes(doc.id));
    const curriculumList: any = [];

    documents.forEach((doc) => {
      curriculumList.push({ id: doc.id, ...doc.data() });
    });

    return curriculumList;
  } catch (err: any) {
    toast.error('Error Retrieving Curriculums!');
    return [];
  }
};

export const searchCurriculum = async (searchQuery: string, page: number) => {
  try {
    if (!searchQuery) {
      throw new Error();
    }

    const response: any = await httpsCallable(functions, 'searchCurriculumByNameV2')({ query: searchQuery });

    const curriculumData = response.data.data;
    const curriculumIds: any = [];
    let curriculumList = [];

    if (curriculumData.length > 0) {
      for (const data of curriculumData) {
        curriculumIds.push(data.id);
      }

      curriculumList = await getMultipleCurriculumByIds(curriculumIds);
      const pageSize = 10;
      const offset = (page - 1) * pageSize;
      const totalPages = Math.ceil(curriculumList.length / pageSize);
      const limitedCareerPaths = curriculumList.slice(offset, offset + pageSize);
      const result: ICurriculumMetaInfo = {
        totalRecords: curriculumList.length,
        currentPage: page,
        curriculums: limitedCareerPaths,
        totalPages: totalPages,
      };
      return result;
    } else {
      return {
        totalRecords: 0,
        currentPage: page,
        curriculums: [],
        totalPages: 0,
      };
    }
  } catch (error) {
    toast.error('Error Searching Curriculum!');
  }
};

export const fetchCurriculums = async () => {
  try {
    const curriculumCollectionRef = collection(db, 'curriculum');
    const snapshots = await getDocs(curriculumCollectionRef);
    const curriculumList: any[] = snapshots.docs.map((doc) => {
      return {
        ...doc.data(),
        id: doc.id,
      };
    });

    return curriculumList;
  } catch (error) {
    toast.error('Error Fetching Curriculum');
    return [];
  }
};
